import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { ChatHistoryHelper } from '../chat/chat-history.helper';
import { ChatHistorySidebarHelper } from '../chat/chat-history-sidebar.helper';
import { DataTransformersHelper } from '../table/data-transformers.helper';

export class ChatSearchHelper {

  static setupSearchDebounce(comp: any) {
    comp.searchDebounce$.pipe(
      debounceTime(300),
      distinctUntilChanged()
    ).subscribe((query: any) => {
      comp.performSearch(query);
    });
  }

  static selectSearchResult(comp: any, result: any) {
    comp._loaderService.showLoader();
    comp.closeChatSearch();
    comp.activeHighlightTerm = comp.chatSearchQuery;

    // Switch Insights on or off depending on where the match is found
    if (result.matchLocation === 'insights') {
      comp.isInsightspopupOpen = true;
    } else if (result.matchLocation === 'data' || result.matchLocation === 'question') {
      comp.isInsightspopupOpen = false;
    }

    comp.loadChatDetails(result.chat.ChatId, result.targetMessageId, result.matchModule);
  }

  static onSearchKeydown(comp: any, event: KeyboardEvent) {
    if (event.key === 'Escape') {
      comp.closeChatSearch();
    } else if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (comp.searchSelectedIndex < comp.filteredSearchChats.length - 1) {
        comp.searchSelectedIndex++;
        comp.scrollSearchSelectionIntoView();
      }
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      if (comp.searchSelectedIndex > 0) {
        comp.searchSelectedIndex--;
        comp.scrollSearchSelectionIntoView();
      }
    } else if (event.key === 'Enter') {
      if (comp.searchSelectedIndex >= 0 && comp.searchSelectedIndex < comp.filteredSearchChats.length) {
        comp.selectSearchResult(comp.filteredSearchChats[comp.searchSelectedIndex]);
      }
    }
  }

  static scrollSearchSelectionIntoView(comp: any) {
    setTimeout(() => {
      const activeEl = document.querySelector('.search-result-item.active');
      if (activeEl) {
        activeEl.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    }, 0);
  }

  static onChatSearch(comp: any) {
    return ChatHistorySidebarHelper.onChatSearch(comp);
  }

  static toggleNestedSearch(comp: any) {
    return ChatHistorySidebarHelper.toggleNestedSearch(comp);
  }

  static tokenizeSearchText(comp: any, searchText: string) {
    return DataTransformersHelper.tokenizeSearchText(searchText);
  }

  static onSearchInput(comp: any, event: any) {
    comp.chatSearchQuery = event.target.value;
    comp.searchDebounce$.next(comp.chatSearchQuery);
  }

  static onSearchSubmit(comp: any) {
    if (comp.isDictating) {
      comp.stopDictation();
    }

    const searchQuery = comp.searchForm.get('query')?.value;
    if (searchQuery && searchQuery.trim()) {
      // Guard: Prevent sending new message while previous question is still processing
      if (comp.arktabaccess) {
        console.warn('[SEND GUARD] Cannot send search query - previous question still processing');
        return;
      }

      comp.isMultiQuestionMode = false;
      comp.inputValue = searchQuery.trim();
      comp.currentView = 'chat';
      comp.selectedMessageId = null;
      comp.isHistorySaving = true;
      comp.sendInputMessage();
      comp.searchForm.patchValue({ query: '' });
    }
  }

  static toggleChatSearch(comp: any) {
    comp.isChatSearchOpen = !comp.isChatSearchOpen;
    if (comp.isChatSearchOpen) {
      comp.chatSearchQuery = '';
      comp.filteredSearchChats = [];
      comp.searchSelectedIndex = -1;
      setTimeout(() => {
        const inputEl = document.getElementById('chat-search-input');
        if (inputEl) inputEl.focus();
      }, 0);
    }
  }

  static onStarredSearch(comp: any) {
    return ChatHistorySidebarHelper.onStarredSearch(comp);
  }

  static closeChatSearch(comp: any) {
    comp.isChatSearchOpen = false;
  }

  static performSearch(comp: any, query: string) {
    const trimmed = query.trim().toLowerCase();
    if (!trimmed) {
      comp.filteredSearchChats = [];
      comp.searchSelectedIndex = -1;
      return;
    }

    const searchTokens = trimmed.split(/\s+/).filter(token => token.length > 0);
    const results = [];

    for (const chat of comp.allChatList) {
      if (!chat.ChatHistory) continue;

      let matchedSnippet = '';
      let hasMatch = false;
      let matchedMsg: any = null;
      let relevanceScore = 0;
      let matchLocation = 'question';
      let matchModule: number | null = null;

      const title = (chat.ChatTitle || '').toLowerCase();
      const titleMatch = searchTokens.every(token => title.includes(token));
      if (titleMatch) {
        relevanceScore = title.includes(trimmed) ? 100 : 50;
      }

      for (const msg of chat.ChatHistory) {
        const q = msg.Question?.toLowerCase() || '';
        const qna = msg.QuestionAnswer?.toLowerCase() || '';

        let insRaw = msg['Insights'] || msg['insights'];

        if (!insRaw && msg.QuestionAnswer) {
          try {
            const parsedQnA = JSON.parse(msg.QuestionAnswer);
            if (parsedQnA && typeof parsedQnA === 'object') {
              const extracted = Object.values(parsedQnA).map((v: any) => {
                return (v?.getSnapshotInsights || '') + ' ' + (v?.getInsights || '') + ' ' + (v?.getSnapshotInsights_Trend || '');
              });
              insRaw = extracted.join(' ');
            }
          } catch (e) {
            console.log(`[SEARCH DEBUG] Failed to parse QnA JSON for fallback.`);
          }
        }

        let insString = Array.isArray(insRaw) ? insRaw.join(' ') : String(insRaw || '');
        const ins = insString.toLowerCase();

        let parsedQnAObj: any = null;
        if (msg.QuestionAnswer) {
          try {
            parsedQnAObj = JSON.parse(msg.QuestionAnswer);
          } catch (e) { }
        }

        const getModuleText = (moduleName: string) => {
          if (!parsedQnAObj || typeof parsedQnAObj !== 'object') return '';
          const mod = parsedQnAObj[moduleName];
          if (!mod) return '';
          return JSON.stringify(mod).toLowerCase();
        };

        const snapshotText = getModuleText('snapshot');
        const brandAnalysisText = getModuleText('brand_analysis') + ' ' + getModuleText('brandanalysis');
        const crosstabText = getModuleText('crosstab');

        const isMsgMatch = searchTokens.every(token => {
          const inQ = q.includes(token);
          const inQna = qna.includes(token);
          const inIns = ins.includes(token);
          return inQ || inQna || inIns;
        });

        if (isMsgMatch) {
          hasMatch = true;
          matchedMsg = msg;
          if (q.includes(trimmed) || qna.includes(trimmed) || ins.includes(trimmed)) {
            relevanceScore = Math.max(relevanceScore, 100);
          } else {
            relevanceScore = Math.max(relevanceScore, 50);
          }

          const getSnippetWindow = (text: string, token: string) => {
            if (!text) return '';
            text = ChatHistoryHelper.createAnswerPreview(text);
            const lowerText = text.toLowerCase();
            const idx = lowerText.indexOf(token);
            if (idx === -1) return text.length > 200 ? text.substring(0, 200) + '...' : text;

            const start = Math.max(0, idx - 60);
            const end = Math.min(text.length, idx + token.length + 140);
            let result = text.substring(start, end);
            if (start > 0) result = '...' + result;
            if (end < text.length) result = result + '...';
            return result;
          };

          const scoreQ = searchTokens.filter(t => q.includes(t)).length;
          const scoreIns = searchTokens.filter(t => ins.includes(t)).length;
          const scoreQna = searchTokens.filter(t => qna.includes(t)).length;

          const scoreSnapshot = searchTokens.filter(t => snapshotText.includes(t)).length;
          const scoreBrandAnalysis = searchTokens.filter(t => brandAnalysisText.includes(t)).length;
          const scoreCrosstab = searchTokens.filter(t => crosstabText.includes(t)).length;

          if (scoreSnapshot >= scoreBrandAnalysis && scoreSnapshot >= scoreCrosstab && scoreSnapshot > 0) {
            matchModule = 1;
          } else if (scoreBrandAnalysis >= scoreCrosstab && scoreBrandAnalysis > 0) {
            matchModule = 2;
          } else if (scoreCrosstab > 0) {
            matchModule = 3;
          }

          if (matchModule) {
          }

          if (scoreIns >= scoreQna && scoreIns >= scoreQ && scoreIns > 0) {
            matchLocation = 'insights';
            const matchToken = searchTokens.find(t => ins.includes(t) && !q.includes(t)) || searchTokens.find(t => ins.includes(t)) || searchTokens[0];
            matchedSnippet = getSnippetWindow(insString, matchToken);
          } else if (scoreQna >= scoreQ && scoreQna > 0) {
            matchLocation = 'data';
            const matchToken = searchTokens.find(t => qna.includes(t) && !q.includes(t)) || searchTokens.find(t => qna.includes(t)) || searchTokens[0];
            matchedSnippet = getSnippetWindow(msg.QuestionAnswer || '', matchToken);
          } else {
            matchLocation = 'question';
            matchedSnippet = msg.Question || '';
          }
          break;
        }
      }

      if (hasMatch || titleMatch) {
        const displayMsg = matchedMsg || (chat.ChatHistory.length > 0 ? chat.ChatHistory[0] : null);
        results.push({
          chat: chat,
          title: (displayMsg && displayMsg.Question) ? displayMsg.Question : (chat.ChatTitle || 'Untitled Chat'),
          snippet: matchedSnippet || 'Match found in chat history',
          date: chat.CreatedAt,
          isStarred: displayMsg ? (displayMsg.IsStarred === true || displayMsg.isStarred === true) : false,
          isLiked: displayMsg ? (displayMsg.IsLiked !== undefined ? displayMsg.IsLiked : displayMsg.isLiked) : null,
          targetMessageId: displayMsg ? displayMsg.MessageId : null,
          relevanceScore: relevanceScore,
          matchLocation: matchedMsg ? matchLocation : 'question',
          matchModule: matchedMsg ? matchModule : null
        });
      }
    }

    // Sort by Relevance first, then Badges (Starred > Liked/Disliked), then by date desc
    results.sort((a, b) => {
      if (a.relevanceScore !== b.relevanceScore) {
        return (b.relevanceScore || 0) - (a.relevanceScore || 0); // Higher relevance comes first
      }
      const getScore = (item: any) => {
        if (item.isStarred) return 3;
        if (item.isLiked === true) return 2;
        if (item.isLiked === false) return 1;
        return 0;
      };

      const scoreA = getScore(a);
      const scoreB = getScore(b);

      if (scoreA !== scoreB) {
        return scoreB - scoreA; // Higher score comes first
      }

      return new Date(b.date || 0).getTime() - new Date(a.date || 0).getTime();
    });

    comp.filteredSearchChats = results;
    comp.searchSelectedIndex = results.length > 0 ? 0 : -1;
  }

}
